import { z } from '../zod';
import { DashboardUrlField } from '../shared/dashboardUrl';
import { AddressValidationSchema } from '../shared/addressValidationSchema';
import { FieldElementValidationEnum, FieldTypeValidationEnum } from '../field/enum';
import { PaymentMethodValidationSchema } from '../shared/paymentMethodValidationSchema';
import {
  SubscriptionAbandonmentEmailStatusValidationEnum,
  SubscriptionStatusValidationEnum,
  SubscriptionPaymentStatusValidationEnum,
  SubscriptionIntervalValidationEnum,
  SubscriptionAbandonmentStatusValidationEnum,
} from './enum';
import { PaginatedListSchema } from '../shared/paginatedList';
import {
  HistoricalTaxRateSnapshotValidationSchema,
  TaxSourceValidationSchema,
  TAX_SOURCE_FIELD_DESCRIPTION,
  TAX_RATES_FIELD_DESCRIPTION,
} from '../tax-rate/create';

export const SubscriptionBillingValidationSchema = z.object({
  name: z.string().nullish(),
  companyName: z.string().nullish(),
  email: z.string().nullish(),
  billingEmail: z.string().nullish(),
  phone: z.string().nullish(),
  address: AddressValidationSchema.nullish(),
});

export const SubscriptionShippingValidationSchema = z.object({
  name: z.string().nullish(),
  phone: z.string().nullish(),
  address: AddressValidationSchema.nullish(),
});

export const FieldSchema = z.object({
  fieldId: z.string().nullish(),
  label: z.string().nullish(),
  value: z.string().nullish(),
  values: z.array(z.string()).nullish().meta({
    description:
      'Chosen option values of a multi-select field (an option without a value is named by its label), in the order the options are defined. `value` holds the same values joined with ", ". The matching labels, as they were when the answer was written, are in `meta.selectedOptionLabels` and joined in `meta.selectedOptionLabel`. Absent on every other field type.',
  }),
  element: FieldElementValidationEnum.nullish(),
  type: FieldTypeValidationEnum.nullish(),
  meta: z.record(z.string(), z.unknown()).nullish(),
  reference: z.string().nullish(),
});

export const SubscriptionFeeSchema = z.object({
  name: z.string().nullish().meta({
    description: 'Name or label for the fee. E.g. `processing fee`.',
  }),
  amount: z.number().nullish().meta({
    description:
      'Fee amount billed each period, in the smallest currency unit. For a fee with both a percentage and a fixed amount, the sum of `percentageAmount` and `fixedAmount`.',
  }),
  percentage: z.number().nullish().meta({
    description: 'Fee percentage, when the fee has one.',
  }),
  percentageAmount: z.number().nullish().meta({
    description:
      'The part of `amount` from the percentage, in the smallest currency unit. Only set on a fee with both a percentage and a fixed amount. Can be 0, e.g. when a 100% coupon leaves nothing for the percentage to apply to.',
  }),
  fixedAmount: z.number().nullish().meta({
    description:
      'The fixed part of `amount`, in the smallest currency unit, already multiplied by quantity or tickets when the fee multiplies. Only set on a fee with both a percentage and a fixed amount.',
  }),
  stripeProductId: z.string().nullish(),
});

export const SubscriptionSetupFeeFeeSchema = z.object({
  name: z.string().nullish(),
  amount: z.number().nullish().meta({
    description: 'Fee amount charged on the setup fee, in the smallest currency unit.',
  }),
  percentage: z.number().nullish().meta({
    description: 'Fee percentage applied to the setup fee.',
  }),
  stripeInvoiceItemId: z.string().nullish(),
});

export const SubscriptionVariantFeeSchema = z.object({
  name: z.string().nullish(),
  amount: z.number().nullish(),
  percentage: z.number().nullish(),
  stripeInvoiceItemId: z.string().nullish(),
});

export const SubscriptionVariantSelectedOptionSchema = z.object({
  optionId: z.string().nullish(),
  optionType: z.enum(['one_time', 'recurring']).nullish(),
  optionName: z.string().nullish(),
  sku: z.string().nullish(),
  quantity: z.number().nullish(),
  additionalChargeAmount: z.number().nullish(),
  discountAmount: z.number().nullish(),
  fees: z.array(SubscriptionVariantFeeSchema).nullish(),
  additionalChargeAmountTotal: z.number().nullish(),
  additionalChargeAmountUsd: z.number().nullish(),
  optionStripePriceId: z.string().nullish(),
  optionStripeProductId: z.string().nullish(),
  optionInvoiceItemId: z.string().nullish(),
  optionInvoiceItemPaid: z.boolean().nullish(),
});

export const SubscriptionVariantSchema = z.object({
  name: z.string().nullish(),
  variantId: z.string().nullish(),
  reference: z.string().nullish(),
  selectionType: z.enum(['single', 'multiple', 'quantity']).nullish(),
  selectedOptions: z.array(SubscriptionVariantSelectedOptionSchema).nullish(),
  fees: z.array(SubscriptionVariantFeeSchema).nullish(),
});

export const SubscriptionCouponSchema = z.object({
  label: z.string().nullish().meta({
    name: 'Label for the coupon, such as "20% off" or "$10 off". This is the label displayed in the dashboard. Not seen by customers.',
  }),
  code: z.string().nullish().meta({
    name: 'The coupon code that the customer entered to receive the discount. E.g. `BLACKFRIDAY`.',
  }),
  amountOff: z.number().nullish().meta({
    name: 'The fixed amount to subtract from the total. Cannot be combined with `percentOff` for the same coupon.',
  }),
  percentOff: z.number().nullish().meta({
    name: 'The percentage to subtract from the total. Cannot be combined with `amountOff` for the same coupon.',
  }),
  appliesToSetupFee: z.boolean().nullish().meta({
    description: 'Whether the coupon discount applies to the setup fee, if any.',
  }),
  duration: z.enum(['once', 'repeating', 'forever']).nullish().meta({
    name: 'The duration of the coupon discount. `once` means the discount applies to the first invoice only. `repeating` means the discount applies for a fixed number of billing periods, as specified by `durationInMonths`. `forever` means the discount applies to all invoices until the subscription is canceled.',
  }),
  durationInMonths: z.number().nullish().meta({
    name: 'If `duration` is `repeating`, the number of billing periods that the discount applies for.',
  }),
  redeemBy: z.iso.datetime().nullish().meta({
    name: 'The date and time when the coupon expires.',
  }),
  maxRedemptions: z.number().nullish().meta({
    name: 'The maximum number of times the coupon can be redeemed. A coupon can be redeemed by the same customer multiple times.',
  }),
  timesRedeemed: z.number().nullish().meta({
    name: 'The number of times the coupon has been redeemed.',
  }),
  currency: z.string().nullish().meta({
    name: 'The currency code for the coupon. Must match the subscription currency. Required if `amountOff` is set.',
  }),
  deleted: z.boolean().nullish().meta({
    name: 'Whether the coupon is deleted.',
  }),
  pageIds: z.array(z.string()).nullish().meta({
    name: 'The list of page IDs to which the coupon applies.',
    description:
      'Deprecated: what a coupon covers is now its `rules`; read them from the coupon (`couponId`). The pages the coupon was limited to when the subscription was bought.',
    deprecated: true,
  }),
});

export const SubscriptionResponseValidationSchema = z.object({
  id: z.string().meta({
    description: 'Unique subscription record ID. Use this value for pagination cursors.',
  }),
  orderId: z.string().nullish().meta({
    description: 'Human-readable order number.',
  }),
  stripeSubscriptionId: z.string().nullish(),
  stripeCustomerId: z.string().nullish(),
  customerEmail: z.string().nullish().meta({
    description: 'Customer email address. Searchable via `search`.',
  }),
  customerName: z.string().nullish().meta({
    description: 'Customer full name. Searchable via `search`.',
  }),
  currency: z.string().nullish().meta({
    description: 'Three-letter currency code.',
  }),
  amount: z.number().meta({
    description:
      'Recurring charge amount per billing period, in the smallest currency unit. Includes recurring fees and any processing fee contribution the customer chose to add to each renewal (`processingFeeContribution.renewalAmount`). It does not reflect coupon discounts; subtract any `couponSnapshot` discount to determine the actual charge.',
  }),
  amountUsd: z.number().nullish().meta({
    description:
      '`amount` converted to USD for multi-currency accounts, so it also includes recurring fees and the renewal processing fee contribution. Coupon discounts are applied in the subscription currency, so do not subtract `amountOff` from `amountUsd`.',
  }),
  taxSource: TaxSourceValidationSchema.nullish().meta({
    description: TAX_SOURCE_FIELD_DESCRIPTION,
  }),
  taxRates: z
    .array(HistoricalTaxRateSnapshotValidationSchema)
    .nullish()
    .meta({ description: TAX_RATES_FIELD_DESCRIPTION }),
  interval: SubscriptionIntervalValidationEnum.nullish().meta({
    description: 'Billing frequency unit, such as `day`, `week`, `month` or `year`.',
  }),
  intervalCount: z.number().nullish().meta({
    description: 'Number of billing intervals between payments.',
  }),
  planIterations: z.number().nullish().meta({
    description: 'If set, the subscription auto-cancels after this many billing cycles.',
  }),
  fees: z.array(SubscriptionFeeSchema).nullish(),
  setupFee: z.number().nullish().meta({
    description: 'Setup fee amount, separate from the recurring subscription amount.',
  }),
  setupFeeDiscountAmount: z.number().nullish(),
  setupFeeFees: z.array(SubscriptionSetupFeeFeeSchema).nullish(),
  setupFeeTotal: z.number().nullish().meta({
    description: 'Total setup fee amount after applying any setup-fee discounts and fees.',
  }),
  setupFeeUsd: z.number().nullish().meta({
    description: 'USD-converted setup fee amount.',
  }),
  setupFeeInvoiceItemId: z.string().nullish(),
  setupFeeInvoiceItemPaid: z.boolean().nullish().meta({
    description: 'Whether the setup fee invoice item has been paid.',
  }),
  quantity: z.number().nullish(),
  productPrice: z.number().nullish().meta({
    description: 'List price for the subscription before discounts.',
  }),
  dynamicPrice: z.number().nullish().meta({
    description:
      'The dynamic price controlled by query parameters. This overrides the `productPrice`.',
  }),
  discountedFromPrice: z.number().nullish().meta({
    description: 'A visual indicator of the pre-discount price. Used as a marketing tool.',
  }),
  sku: z.string().nullish(),
  billing: SubscriptionBillingValidationSchema.nullish().meta({
    description: 'Billing details, including name, email, phone, and address.',
  }),
  shipping: SubscriptionShippingValidationSchema.nullish().meta({
    description: 'Shipping details, including name, phone, and address.',
  }),
  fields: z.array(FieldSchema).nullish().meta({
    description: 'Form field responses captured during checkout.',
  }),
  variants: z.array(SubscriptionVariantSchema).nullish(),
  couponId: z.string().nullish().meta({
    description: 'Unique coupon ID when a coupon was applied.',
  }),
  couponSnapshot: SubscriptionCouponSchema.nullish().meta({
    description:
      'Immutable snapshot of the coupon at purchase time, including the code, label, discount amount or percent, duration, and related metadata. Present only when a coupon was applied.',
  }),
  licenseKeyId: z.string().nullish(),
  livemode: z.boolean().nullish(),
  status: SubscriptionStatusValidationEnum.nullish().meta({
    description: `Subscription lifecycle status.
    \`active\`: healthy and billing normally.
    \`past_due\`: still active, but payment is failing and Stripe is retrying; include this when counting active subscriptions.
    \`trialing\`: free trial, not yet charged, and not considered active for counting.
    \`scheduled\`: billing starts in the future and is not considered active for counting.
    \`canceled\`: subscription ended; check \`canceledAt\`.
    \`failed\`: non-recoverable payment failure.
    \`draft\`: checkout form opened but not submitted.
    \`draft_expired\`: draft expired without completion.
    \`incomplete\`: awaiting payment action and may still recover.
    \`incomplete_expired\`: incomplete subscription expired.
    \`paused\`: billing suspended.
    \`completed\`: fixed-term subscription finished after all \`planIterations\` were collected.`,
  }),
  paymentStatus: SubscriptionPaymentStatusValidationEnum.nullish().meta({
    description:
      'Secondary payment or invoice state. Common values include `succeeded` or `paid` (payment healthy), `requires_payment_method` (payment failing), and `requires_action` (customer authentication required). This can help explain why a subscription is in a given state, but `status` is the primary indicator of subscription health.',
  }),
  paymentError: z.string().nullish().meta({
    description:
      'Last payment error message. This value may persist after a later successful payment, so always check `status` before interpreting `paymentError`.',
  }),
  paymentMethod: PaymentMethodValidationSchema.nullish().meta({
    description:
      'Payment method details, including `gateway`, `method`, `brand`, `last4`, `country`, `funding`, and optionally `wallet`.',
  }),
  locale: z.string().nullish(),
  sessionId: z.string().nullish().meta({
    description: 'Unique session ID.',
  }),
  clientIp: z.string().nullish().meta({
    description: 'Customer IP address at checkout.',
  }),
  userAgent: z.string().nullish().meta({
    description: "Customer's browser user agent at checkout.",
  }),
  screenWidth: z.number().nullish().meta({
    description: "Customer's screen width in pixels at checkout.",
  }),
  visitId: z.string().nullish().meta({
    description: 'Unique visit ID.',
  }),
  queryParameters: z.record(z.string(), z.string()).nullish().meta({
    description: 'Key-value pairs of query parameters from the checkout URL.',
  }),
  stripePaymentIntentId: z.string().nullish(),
  stripeSetupIntentId: z.string().nullish(),
  startDate: z.iso.datetime().nullish().meta({
    description:
      'When recurring billing begins. For trial subscriptions, this typically matches `trialEnd`.',
  }),
  trialEnd: z.iso.datetime().nullish().meta({
    description: 'End of the trial period, if any.',
  }),
  trialStart: z.iso.datetime().nullish().meta({
    description: 'Start of the trial period, if any.',
  }),
  cancelAt: z.iso.datetime().nullish().meta({
    description: 'Scheduled future cancellation date, if one has been set.',
  }),
  cancelAtPeriodEnd: z.boolean().nullish().meta({
    description:
      'Whether the subscription is set to cancel at the end of the current billing period.',
  }),
  currentPeriodStart: z.iso.datetime().nullish().meta({
    description: 'Start of the current billing period.',
  }),
  currentPeriodEnd: z.iso.datetime().nullish().meta({
    description:
      'End of the current billing period. For active subscriptions, this is effectively the next billing date.',
  }),
  upsell: z.boolean().nullish(),
  upsellPageId: z.string().nullish().meta({
    description:
      'For an upsell record, the ID of the page the buyer paid on before accepting the upsell (the start of the funnel).',
  }),
  upsellChargeId: z.string().nullish().meta({
    description: 'For an upsell record, the ID of the payment made on the page before the upsell.',
  }),
  upsellSubscriptionId: z.string().nullish().meta({
    description:
      'For an upsell record, the ID of the subscription created on the page before the upsell.',
  }),
  sellerId: z.string().nullish(),
  customerId: z.string().nullish().meta({
    description: 'Customer ID.',
  }),
  pageId: z.string().nullish().meta({
    description: 'Page ID.',
  }),
  productId: z.string().nullish().meta({
    description: 'Product ID.',
  }),
  productTitle: z.string().nullish().meta({
    description: 'Human-readable product or plan name. Searchable via `search`.',
  }),
  lastRefundAt: z.iso.datetime().nullish().meta({
    description: 'Timestamp of the most recent refund, if any.',
  }),
  lastRefundReason: z.string().nullish().meta({
    description: 'Reason code for the most recent refund, if recorded.',
  }),
  lastRefundReasonNote: z.string().nullish().meta({
    description: 'Merchant note for the most recent refund, if recorded.',
  }),
  canceledAt: z.iso.datetime().nullish().meta({
    description: 'Timestamp of cancellation, if the subscription has been canceled.',
  }),
  canceledBy: z.string().nullish().meta({
    description:
      'Who initiated the cancellation, if recorded. Only recorded from the Checkout Page dashboard.',
  }),
  reason: z.string().nullish().meta({
    description:
      'Cancellation reason, if captured. Only recorded from the Checkout Page dashboard.',
  }),
  isAbandoned: z.boolean().nullish().meta({
    description:
      'Whether the subscription was abandoned. Check `recoveredAt` and `abandonmentStatus` to see if the subscription was later recovered.',
  }),
  recoveredAt: z.iso.datetime().nullish().meta({
    description:
      'The ISO timestamp of when the subscription was recovered, if it was abandoned and later recovered.',
  }),
  abandonmentStatus: SubscriptionAbandonmentStatusValidationEnum.nullish().meta({
    description:
      'Abandonment or recovery status. `abandoned` means the subscription was abandoned and has not been recovered. `recovered` means the subscription was abandoned but later recovered.',
  }),
  abandonedCartEmailStatus: SubscriptionAbandonmentEmailStatusValidationEnum.nullish().meta({
    description: `The status of the abandonment email sent for this subscription.
    \`pending\`: No email has been sent. Stays as pending if isAbandoned is false.
    \`first_sent\`: The first abandonment email has been sent.
    \`second_sent\`: The second abandonment email has been sent.
    \`third_sent\`: The third abandonment email has been sent.
    \`skipped\`: The abandonment email processed was skipped.`,
  }),
  abandonedCartEmailSentAt: z.iso.datetime().nullish().meta({
    description: 'The ISO timestamp of when the abandonment email was sent, if applicable.',
  }),
  pageSlug: z.string().nullish(),
  createdAt: z.iso.datetime().meta({
    description: 'ISO 8601 timestamp for when the subscription record was created.',
  }),
  updatedAt: z.iso.datetime().meta({
    description: 'ISO 8601 timestamp for when the subscription record was last updated.',
  }),
});
export const GetSubscriptionsResponseValidationSchema = PaginatedListSchema(
  SubscriptionResponseValidationSchema,
);

export const SubscriptionDetailResponseValidationSchema = z
  .object({
    data: SubscriptionResponseValidationSchema,
  })
  .meta({ id: 'SubscriptionDetailResponse', description: 'Envelope containing one subscription.' });

export const SubscriptionToolDataValidationSchema = SubscriptionResponseValidationSchema.extend({
  dashboardUrl: DashboardUrlField,
});

export const SubscriptionDetailToolResponseValidationSchema = z.object({
  data: SubscriptionToolDataValidationSchema,
});

export const SubscriptionsToolResponseValidationSchema = PaginatedListSchema(
  SubscriptionToolDataValidationSchema,
);
